import { isEqual } from 'lodash';
import { ref } from 'vue';

import { getCountryByShortName, TCountry } from '@/constants/countries';
import { i18n } from '@/i18n';
import { Onboarding } from '@/services/requests/onboarding/Onboarding.types';
import { arrayToObj } from '@/utils/arrayToObj';

export const getCountryName = (shortName: string | undefined) => getCountryByShortName(shortName)?.name;
export const getPersonalCountryName = (shortName: string | undefined) => getCountryByShortName(shortName)?.name;

export function transformDashboardAddress(address?: Onboarding.Address, withShortCountry = false) {
  if (!address) return undefined;

  const country = withShortCountry
    ? address.countryIso3
    : getCountryName(address.countryIso3);

  return [
    address.addressLine1,
    address.addressLine2,
    `${address.postalCode} ${address.city}`,
    country,
  ].filter((el) => el?.trim()).join(', ');
}

export function transformVerifyAddress(address?: Onboarding.Address, withShortCountry = false) {
  if (!address) return undefined;
  return [
    address.addressLine1,
    address.addressLine2,
    address.city,
    address.postalCode,
    withShortCountry ? address.countryIso3 : getCountryName(address.countryIso3),
  ].filter((el) => el && el?.trim()).join(', ');
}

export const getDate = (date?: string) => {
  if (!date) return '';
  const d = new Date(date);
  const format = (...args: number[]) => args.map((arg) => `${arg}`.padStart(2, '0'));
  return [...format(d.getDate(), d.getMonth() + 1), d.getFullYear()].join('.');
};

export function dateToLocalDate(dateString?: string) {
  if (dateString === undefined) return undefined;
  const date = new Date(dateString);
  return date.toLocaleDateString();
}

export function transformPurposeSet(purposes?: readonly Onboarding.Business.Purpose[]) {
  if (purposes === undefined) return undefined;
  return purposes.map((purpose) => i18n.global.t(`entity.Purpose.${purpose}`)).join(', ');
}

export function transformAccountCurrencies(accountCurrencies?: readonly Onboarding.AccountCurrency[]) {
  if (accountCurrencies === undefined || accountCurrencies.length === 0) return undefined;
  return accountCurrencies.map((purpose) => i18n.global.t(`entity.AccountCurrency.${purpose}`)).join(', ');
}

export function getCountryFunctions(countries: TCountry[], countryKey: keyof TCountry) {
  const mappedCountries = arrayToObj(countries, countryKey);

  function getCountryNameByShortName(countryShortName?: string) {
    if (countryShortName === undefined) return undefined;
    return mappedCountries[countryShortName]?.name;
  }
  function transformCountries(countriesShortNames?: readonly string[]) {
    if (countriesShortNames === undefined) return undefined;
    return countriesShortNames?.map((country) => (mappedCountries[country] ? mappedCountries[country].name : country)).join(', ');
  }

  return {
    getCountryNameByShortName,
    transformCountries,
  };
}

export function transformTurnover(turnover?: Onboarding.AnnualTurnover) {
  return turnover && i18n.global.t(`entity.AnnualTurnover.${turnover}`);
}

export function transformFundsSource(fundsSource?: Onboarding.FundsSource) {
  return fundsSource && i18n.global.t(`entity.FundsSource.${fundsSource}`);
}

export function getStageIndexMeta(stageIndex: number) {
  return {
    layout: {
      props: {
        stageIndex,
      },
    },
  };
}

export function checkIsAddressesSame(
  mainAddress?: Onboarding.Address,
  mailingAddress?: Onboarding.Address,
) {
  return !mainAddress || !mailingAddress || isEqual(mainAddress, mailingAddress);
}

export function useDoubleOnboardingAddressForm(
  resetSecondAddressForm: (newSecondAddressFormValue?: Onboarding.Address) => void,
  initialMainAddress?: Onboarding.Address,
  initialSecondAddress?: Onboarding.Address,
) {
  const isAddressesSame = ref(checkIsAddressesSame(initialMainAddress, initialSecondAddress));

  function resetSecondAddress(newIsAddressesSame?: boolean) {
    resetSecondAddressForm(newIsAddressesSame
      ? undefined
      : initialSecondAddress ?? initialMainAddress);
  }

  return {
    isAddressesSame,
    resetSecondAddress,
  };
}

export function getAccountTypeString(applicationType?: Onboarding.ApplicationType, partnerAttributes?: Onboarding.PartnerAttributes) {
  if (!applicationType) return '';
  const customerType = partnerAttributes?.partnerId ? 'PERSONAL_PAPI' : applicationType;
  return `entity.OnboardingCustomerType.${customerType}`;
}
